import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddressAutocomplete — one-line "Start typing…" street input backed by
 * Google Places (server-side proxy so the key never hits the browser).
 *
 * On select, calls `onResolve({street1, street2, city, state, zip, country})`
 * with fully-populated fields. Parent should overwrite the address block.
 *
 * Props:
 *   value        current street text
 *   onChange     (v) => void   — user typing
 *   onResolve    (addr) => void  — full address picked
 *   testid       for e2e
 *   placeholder
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onResolve,
  testid = 'addr-autocomplete',
  placeholder = 'Start typing an address…',
}) {
  // The address lookup spends the Google key, so it needs a session.
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [q, setQ] = useState(value || '');
  const [preds, setPreds] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const sessionToken = useRef(null);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => { setQ(value || ''); }, [value]);

  // click-away to close
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runAutocomplete = async (text) => {
    if (!text || text.trim().length < 3) { setPreds([]); return; }
    setBusy(true);
    try {
      const url = new URL(`${API}/geo/places/autocomplete`, window.location.origin);
      url.searchParams.set('q', text);
      if (sessionToken.current) url.searchParams.set('sessiontoken', sessionToken.current);
      const r = await fetch(url.toString(), { headers: auth });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      if (!sessionToken.current) sessionToken.current = j.sessiontoken;
      setPreds(j.predictions || []);
      setOpen(true);
    } catch {
      setPreds([]);
    } finally { setBusy(false); }
  };

  const onType = (v) => {
    setQ(v);
    onChange && onChange(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runAutocomplete(v), 200);
  };

  const pick = async (p) => {
    setOpen(false);
    setBusy(true);
    try {
      const url = new URL(`${API}/geo/places/details`, window.location.origin);
      url.searchParams.set('place_id', p.place_id);
      if (sessionToken.current) url.searchParams.set('sessiontoken', sessionToken.current);
      const r = await fetch(url.toString(), { headers: auth });
      if (!r.ok) throw new Error(await r.text());
      const detail = await r.json();
      sessionToken.current = null;  // reset session after a resolve, per Google billing rules
      setQ(detail.street1 || p.description);
      onChange && onChange(detail.street1 || '');
      onResolve && onResolve(detail);
    } catch {
      // fallback: just accept the raw description
      setQ(p.description || '');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={rootRef}>
      <div className="relative">
        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => preds.length && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className="pl-9"
          data-testid={testid}
        />
        {busy && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      </div>
      {open && preds.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
          {preds.map((p) => (
            <button
              type="button"
              key={p.place_id}
              onClick={() => pick(p)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
              data-testid={`${testid}-opt-${p.place_id.slice(0, 8)}`}
            >
              <div className="text-sm font-medium">{p.main_text}</div>
              <div className="text-xs text-slate-500">{p.secondary_text}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
